import { useState, useCallback, useEffect, useRef } from 'react';
import { checkExcludedPayer, searchPayerLookup, searchPayeeLookup } from '../../../../redux/grid/action';
import { trackFieldEditOnBlur, updateFormValues } from '../utils';
import { NEW_QA_REVIEW } from '../../../../routeNames.js';

const useF7PayerPayeeLookup = ({
  formEob,
  jobData,
  isViewOnllyGrid,
  handleChangeGridPosition,
  setLoading,
  updateMasterValues,
  setDoubleClicked, // Added for payee functionality
  showAlert,
  setSelectedPayerExce,
  eobFormSetValues,
  eobFormGetValues,
  triggerEobValidation,
  resoncodeDropDrown,
  additionalInfoGetValues,additionalInfoSetValue
  
}) => {
  // State management
  const excludePayerLockRef = useRef(false);
  const [payerPayeeF7Type, setPayerPayeeF7Type] = useState(null);
  const [payerPayeeLookup, setPayerPayeeLookup] = useState(false);
  const [payerPayeeValue, setPayerPayeeValue] = useState("");
  const [payerPayeeIndex, setPayerPayeeIndex] = useState(0);
  const [payerPayeeData, setPayerPayeeData] = useState(null);
  const [payerPullPath, setPayerPullPath] = useState(null);

  // Toggle payer/payee lookup modal
  const handlePayerPayeeLookupSearch = useCallback(() => {
        if (!isViewOnllyGrid) {
            handleChangeGridPosition("payer_payee_popup");
            setPayerPayeeLookup(prev => !prev);
            const fieldValue = payerPayeeF7Type === 'payer' ? formEob.values.payer_name : formEob.values.payee_name;
            setPayerPayeeValue(fieldValue || "");
            setPayerPayeeData(null);
        }
  }, [isViewOnllyGrid, handleChangeGridPosition, formEob.values.payer_name, formEob.values.payee_name, payerPayeeF7Type]);

  // Search payer/payee API
  const handleSearchPayerPayee = useCallback(async () => {
    if (!payerPayeeValue || payerPayeeValue.length < 3) {
        setLoading(false);
        setPayerPayeeData(null);
        return;
    }

    setLoading(true);
    const searchLookup = payerPayeeF7Type === "payer" ? searchPayerLookup : searchPayeeLookup;
    
    try {
      await searchLookup({ 
        search: payerPayeeValue.toUpperCase(), 
        client: jobData?.client_id 
      })((response) => {
        setLoading(false);
        if (!response?.data?.status && response?.status) {
          setPayerPayeeData(response?.data?.results || null);
        } else {
          setPayerPayeeData(null);
        }
      });
    } catch (error) {
      setLoading(false);
      setPayerPayeeData(null);
      console.error('Payer/Payee search error:', error);
    }
  }, [payerPayeeValue, jobData?.client_id, searchPayerLookup, searchPayeeLookup, setLoading, payerPayeeF7Type]);

  // Handle hover on payer/payee list items
  const togglePayerPayeeHoverEnter = useCallback((index) => {
    setPayerPayeeIndex(index);
  }, []);

  // Generate updated values based on type (payer or payee)
  const typeUpdatedValues = useCallback((selected) => {
    const oldValues = eobFormGetValues()
    const prefix = payerPayeeF7Type.toLowerCase(); // "payer" or "payee"
    let updates = {
        ...formEob.values,
        [`${prefix}_id`]: selected.id || "",
        [`${prefix}_pulled_id`]: selected.id || "",
        [`${prefix}_name`]: selected.name || "",
        [`${prefix}_address`]: selected.address || "",
        [`${prefix}_city`]: selected.city || "",
        [`${prefix}_state`]: selected.state || "",
        [`${prefix}_zip`]: selected.zip || "",
        
    };

    if (prefix === "payee") {
        updates = {
        ...updates,
        payee_npi: selected?.n1_identification_code || "",
        tax_identification_number: selected?.tax_identification_number || "",
      
        };
    }
    const trackedData = {
    [`${prefix}_name`]: oldValues?.[`${prefix}_name`],
    [`${prefix}_address`]: oldValues?.[`${prefix}_address`],
    [`${prefix}_city`]: oldValues?.[`${prefix}_city`],
    [`${prefix}_state`]: oldValues?.[`${prefix}_state`],
    [`${prefix}_zip`]: oldValues?.[`${prefix}_zip`],
    ...(prefix === "payee"
        ? {
            payee_npi: oldValues?.payee_npi,
            tax_identification_number: oldValues?.tax_identification_number,
        }
        : {}),
    };

    return {
      updatedValues:{...updates},
      trackedData: trackedData,
    };

  }, [formEob, payerPayeeF7Type,eobFormGetValues]);

  const setingPayerOptimized = (selectedPayer) => {

    //   if (state.Payment835 && payerPullPath?.from === 'EOB') {
        const excludedPayerReason = resoncodeDropDrown?.find(item => item.label === "Excluded Payer");
        const currentValues = eobFormGetValues();
        const qdx_reason = currentValues?.qdx_reason||[]
        const hasExcludedPayer = qdx_reason?.some(item => item?.label === "Excluded Payer") || false;

        // Prepare updates for main form
        const mainFormUpdates = {
        payer_id: selectedPayer?.id || "",
        payer_pulled_id: selectedPayer?.id || "",
        payer_name: selectedPayer?.name || "",
        payer_address: selectedPayer?.address || "",
        payer_city: selectedPayer?.city || "",
        payer_state: selectedPayer?.state || "",
        payer_zip: selectedPayer?.zip || "",
      
        qdx_status: "INCOMPLETE",
        qdx_reason: (excludedPayerReason && !hasExcludedPayer) ? 
            [...(currentValues.qdx_reason || []), excludedPayerReason] : 
            (currentValues.qdx_reason || []),
        };

        // Prepare updates for additional info form
        const additionalInfoValues = additionalInfoGetValues();
        const additionalInfoUpdates = {
        status: "INCOMPLETE",
        reason: (excludedPayerReason && !hasExcludedPayer) ? 
            [...(additionalInfoValues.reason || []), excludedPayerReason] : 
            (additionalInfoValues.reason || []),
        };

        // Apply updates
        updateFormValues(eobFormSetValues, currentValues, mainFormUpdates);
        updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);

        handlePayerPayeeLookupSearch();
    //   }
    // ... similar pattern for other conditions
};

  // Activate selected payer/payee
  const activatePayerPayeeLookup = useCallback(async () => {
    if (!payerPayeeData || !payerPayeeData[payerPayeeIndex]) return;
    
    await insertSelectedPayerPayee(payerPayeeData[payerPayeeIndex]);
  }, [payerPayeeData, payerPayeeIndex]);

  // Insert selected payer (with excluded payer check)
  const insertSelectedPayer = useCallback(async (selectedPayer) => {
    if (!selectedPayer?.name || excludePayerLockRef.current) {
      return;
    }
    if (!jobData?.job || !selectedPayer?.id) {
      return;
    }

    excludePayerLockRef.current = true;

    try {
      checkExcludedPayer(jobData.job, selectedPayer.id, jobData?.facility_id)((response) => {
        if (!response?.status) return;
        excludePayerLockRef.current = false;
        
        if (response.is_excluded_payer) {
          // Handle excluded payer
          setSelectedPayerExce(selectedPayer);
          setPayerPullPath({ from: "EOB" });
          showAlert('payerExcluded',"Payer is Excluded",null,null,setingPayerOptimized(selectedPayer),setPayerPullPath(null));

        } else {
          // Update form with selected payer
          const {updatedValues,trackedData} = typeUpdatedValues(selectedPayer);

          // Handle QDX status for excluded payer
          const isIncompleteWithExcludedPayer = 
            formEob?.values?.qdx_status === 'INCOMPLETE' && 
            formEob?.values?.qdx_reason?.some(item => item?.label === 'Excluded Payer');

          if (isIncompleteWithExcludedPayer) {
            updatedValues.qdx_status = "";
            updatedValues.qdx_reason = formEob.values.qdx_reason.filter(
              item => item?.label !== 'Excluded Payer'
            );
          }

          updateMasterValues(updatedValues);
          Object.keys(trackedData).forEach((fields)=>{
          trackFieldEditOnBlur({
                    name: fields,
                    oldValue: trackedData?.[fields],   // always the API-loaded initial value
                    setValue:eobFormSetValues,
                    getValues:eobFormGetValues,
                });
            })
          triggerEobValidation()
          handlePayerPayeeLookupSearch(); // Close the lookup modal
        }
      });
    } catch (error) {
      excludePayerLockRef.current = false;
      console.error('Error checking excluded payer:', error);
    }
  }, [
    jobData,
    formEob,
    checkExcludedPayer,
    handlePayerPayeeLookupSearch,
    excludePayerLockRef,
    typeUpdatedValues,
    updateMasterValues,
    triggerEobValidation,
    eobFormSetValues,
    eobFormGetValues
  ]);

  // Insert selected payee (direct insertion without excluded check)
  const insertSelectedPayee = useCallback(async (selectedPayee) => {
    if (selectedPayee?.name) {
      const {updatedValues,trackedData} = typeUpdatedValues(selectedPayee);
      updateMasterValues(updatedValues);
      triggerEobValidation()
       Object.keys(trackedData).forEach((fields)=>{
          trackFieldEditOnBlur({
                    name: fields,
                    oldValue: trackedData?.[fields],   // always the API-loaded initial value
                    setValue:eobFormSetValues,
                    getValues:eobFormGetValues,
                });
            })
      // Close the lookup modal
      await handlePayerPayeeLookupSearch();
    } else {
      if (setDoubleClicked) {
        setDoubleClicked(false);
      }
    }
  }, [triggerEobValidation,typeUpdatedValues, updateMasterValues, handlePayerPayeeLookupSearch, setDoubleClicked,eobFormSetValues,eobFormGetValues]);

  // Common insert function that routes to appropriate handler
  const insertSelectedPayerPayee = useCallback(async (selected) => {
    if (payerPayeeF7Type === 'payer') {
      await insertSelectedPayer(selected);
    } else if (payerPayeeF7Type === 'payee') {
      await insertSelectedPayee(selected);
    }
  }, [payerPayeeF7Type, insertSelectedPayer, insertSelectedPayee]);

  // Update payer/payee search value
  const updatePayerPayeeValue = useCallback((value) => {
    setPayerPayeeValue(value);
  }, []);

  // Clear payer/payee data
  const clearPayerPayeeData = useCallback(() => {
    setPayerPayeeData(null);
    setPayerPayeeValue("");
    setPayerPayeeIndex(0);
  }, []);

  // Reset all states
  const resetPayerPayeeLookup = useCallback(() => {
    setPayerPayeeLookup(false);
    setPayerPayeeValue("");
    setPayerPayeeIndex(0);
    setPayerPayeeData(null);
    setSelectedPayerExce(null);
    setPayerPullPath(null);
    setPayerPayeeF7Type(null);
  }, []);

  // Handler: Keyup for F7
  const handlePayerPayeeF7KeyUp = useCallback(
    (event, is_search) => {
      const activeElementName = document.activeElement?.getAttribute('name');
      
      if (
        (activeElementName === 'payer_name' || activeElementName === 'payee_name') &&
        event.keyCode === 118 && // F7
        !isViewOnllyGrid
      ) {
        handleChangeGridPosition("payer_payee_popup");
        setPayerPayeeLookup(true);
        setPayerPayeeValue(event.target.value || "");
        setPayerPayeeF7Type(activeElementName === 'payer_name' ? 'payer' : 'payee');
      } else if (is_search === true) {
        handlePayerPayeeLookupSearch();
      }
    },
    [isViewOnllyGrid, setPayerPayeeLookup, handleChangeGridPosition, handlePayerPayeeLookupSearch]
  );

  // Handle close functionality
  const handleClose837PayerPayee = useCallback(
    (event) => {
      if (payerPayeeLookup) {
        setPayerPayeeData("");
        setPayerPayeeLookup(false);
        setPayerPayeeF7Type(null);
        setTimeout(() => {
          const fieldName = payerPayeeF7Type === 'payer' ? 'payer_name' : 'payee_name';
          document.querySelector(`[name="${fieldName}"]`)?.focus();
        }, 50);
      }
    },
    [payerPayeeLookup, setPayerPayeeData, payerPayeeF7Type]
  );

  // Keyboard navigation effect
  useEffect(() => {
    if (!payerPayeeLookup) return; // Only add listener if lookup is active

    const handleKeyUp = (event) => {
      if (event.key === "Escape" || event.keyCode === 27) {
        handleClose837PayerPayee(event);
      } else if (payerPayeeLookup && event.keyCode === 38) {
        // Arrow Up
        if (payerPayeeIndex > 0) setPayerPayeeIndex(payerPayeeIndex - 1);
      } else if (payerPayeeLookup && event.keyCode === 40) {
        // Arrow Down
        if (payerPayeeIndex < payerPayeeData?.length - 1) setPayerPayeeIndex(payerPayeeIndex + 1);
      } else if (payerPayeeLookup && event.keyCode === 13) {
        // Enter
        if (event.ctrlKey === true) {
          activatePayerPayeeLookup();
        }
      }
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [payerPayeeLookup, handleClose837PayerPayee, payerPayeeIndex, setPayerPayeeIndex, activatePayerPayeeLookup, payerPayeeData]);

  return {
    // States
    payerPayeeLookup,
    payerPayeeValue,
    payerPayeeIndex,
    payerPayeeData,
    payerPullPath,
    payerPayeeF7Type,
    
    // Actions
    handlePayerPayeeLookupSearch,
    handleSearchPayerPayee,
    togglePayerPayeeHoverEnter,
    activatePayerPayeeLookup,
    insertSelectedPayerPayee,
    insertSelectedPayer,
    insertSelectedPayee,
    updatePayerPayeeValue,
    clearPayerPayeeData,
    resetPayerPayeeLookup,
    handlePayerPayeeF7KeyUp,

    // State setters (if needed for external control)
    setPayerPayeeLookup,
    setPayerPayeeIndex,
    setPayerPullPath,
    setPayerPayeeValue,
    
  };
};

export default useF7PayerPayeeLookup;